import React, { ReactNode, useState } from "react";
import { Trans } from "react-i18next";

import { useTranslation } from "lib/i18n/use-translation";

import styles from "./company-dashboard.module.scss";

const SUPPORT_EMAIL = "support@walkingonearth.com";

export const liveBannerDismissedKey = (companyId: number) =>
  `wone.dashboard.liveBanner.dismissed.${companyId}`;

// Storage can be blocked (private windows, cleared site data). Losing the
// remembered dismissal only means the banner shows again, so that is the
// recovery rather than an error.
const readDismissed = (companyId: number | null): boolean => {
  if (companyId === null) return false;
  try {
    return (
      window.localStorage.getItem(liveBannerDismissedKey(companyId)) === "1"
    );
  } catch {
    return false;
  }
};

const rememberDismissed = (companyId: number | null) => {
  if (companyId === null) return;
  try {
    window.localStorage.setItem(liveBannerDismissedKey(companyId), "1");
  } catch {
    // Closed for this visit only.
  }
};

const Icon = ({ children }: { children: ReactNode }) => (
  <svg
    className={styles["live-banner-icon"]}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const PeopleIcon = () => (
  <Icon>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

const PulseIcon = () => (
  <Icon>
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </Icon>
);

const TrendingUpIcon = () => (
  <Icon>
    <path d="M22 7 13.5 15.5 8.5 10.5 2 17" />
    <path d="M16 7h6v6" />
  </Icon>
);

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CARDS = [
  { key: "working", icon: <PeopleIcon /> },
  { key: "help", icon: <PulseIcon /> },
  { key: "return", icon: <TrendingUpIcon /> },
] as const;

const LiveBanner = ({ companyId }: { companyId: number | null }) => {
  const { t } = useTranslation(["admin"]);
  const [dismissed, setDismissed] = useState(() => readDismissed(companyId));

  if (dismissed) return null;

  const close = () => {
    rememberDismissed(companyId);
    setDismissed(true);
  };

  return (
    <section
      className={styles["live-banner"]}
      aria-labelledby="dashboard-live-banner-title"
    >
      <button
        type="button"
        className={styles["live-banner-close"]}
        onClick={close}
        aria-label={t("admin:companyDashboard.liveBanner.close")}
      >
        <CloseIcon />
      </button>
      <p
        id="dashboard-live-banner-title"
        className={styles["live-banner-title"]}
      >
        {t("admin:companyDashboard.liveBanner.title")}
      </p>
      <ul className={styles["live-banner-cards"]}>
        {CARDS.map(({ key, icon }) => (
          <li key={key} className={styles["live-banner-card"]}>
            {icon}
            <p className={styles["live-banner-card-title"]}>
              {t(`admin:companyDashboard.liveBanner.${key}.title`)}
            </p>
            <p>{t(`admin:companyDashboard.liveBanner.${key}.body`)}</p>
          </li>
        ))}
      </ul>
      <p className={styles["live-banner-contact"]}>
        <Trans
          i18nKey="admin:companyDashboard.liveBanner.contact"
          components={{
            email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>,
          }}
        />
      </p>
    </section>
  );
};

export default LiveBanner;
